import { AutoComplete, Input, InputRef, Row, Spin } from 'antd';
import { AxiosResponse } from 'axios';
import clsx from 'clsx';
import Text from 'components/Text';
import debounce from 'lodash/debounce';
import { forwardRef, memo, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import apis from 'services/apis';
import shopSelectors from 'services/selectors/shop';
import { ICustomerResItem } from 'services/types/response';
import styled from 'styled-components';
import { ICustomerUI } from 'types/customer';
import { formatPhone } from 'utils/formatPhone';
import storage from 'utils/sessionStorage';
import NotFoundCustomerBox from './NotFoundCustomerBox';
import ic_close from './ic_close.svg';
import ic_search from './ic_search.svg';
const IconSearch = () => <img src={ic_search} alt="ic_search" style={{ width: 24, height: 24 }} />;
const IconClose = () => <img src={ic_close} alt="ic_close" style={{ width: 24, height: 24 }} />;

export const unknownCustomerId = '_unknownCustomerId';
export const unknownCustomerItem: ICustomerUI = {
  id: unknownCustomerId,
  name: 'Unknown Customer',
  phone: '0000000000',
  point: 0,
  note: '',
};
export const maskPhone = (val: string) => {
  if (!val) return '';
  return '(***) ***-' + val.slice(val.length - 4, val.length);
};
type ICusOption = {
  value: string;
  name: string;
  keywords: string[];
  label: JSX.Element;
};
type CustomerSearchInputProps = {
  v2NewApt?: boolean;
  value?: ICustomerUI | null;
  onChange?: (data: ICustomerUI | null) => void;
  preventUnknown?: boolean;
  autoOpenAddWhenNotFound?: boolean;
  v2?: boolean;
  useForService?: boolean;
  validate?: (newData: ICustomerUI) => boolean;
  className?: string;
};

type Ref = {
  open: () => void;
};
export const useCustomerSearchInputRef = () => useRef<Ref>(null);

const CustomerSearchInput = forwardRef<Ref, CustomerSearchInputProps>(({ className, validate: validateProp, useForService, v2, onChange = () => undefined, value, preventUnknown, autoOpenAddWhenNotFound = false }, ref) => {
  const inputRef = useRef<InputRef>(null);
  const [open, setOpen] = useState(false);
  const customerStore = shopSelectors.customers();
  const [customersFilter, setCustomersFilter] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const initialOptions = useMemo(() => {
    const data: ICusOption[] = customerStore.map((o) => {
      const phone = o?.phone || '';
      return {
        value: o.id?.toString() || '',
        name: o.name || '',
        keywords: [o.name?.toLocaleLowerCase(), phone],
        label: renderLabel(o.name, phone, []),
      };
    });
    return data;
  }, [customerStore, preventUnknown]);

  const [searchText, setSearchText] = useState<string>('');

  const validate = (newData: ICustomerUI) => validateProp ? validateProp(newData) : true;

  const optionsFilter = useMemo(() => {
    return customersFilter.map((o) => {
      const phone = o?.phone || '';
      return {
        value: o.id?.toString() || '',
        name: o.name,
        label: renderLabel(o.name, phone),
      };
    });
  }, [customersFilter, searchText]);

  const options = useMemo(() => {
    if (!searchText) return initialOptions;
    return optionsFilter;
  }, [initialOptions, searchText, optionsFilter]);

  const handleSwitchCustomer = (data: string) => {
    inputRef.current?.blur();
    setSearchText('');
    if (data === unknownCustomerId) {
      onChange(unknownCustomerItem);
      return;
    }
    const result = (searchText ? customersFilter : customerStore)?.find((item) => item.id?.toString() === data?.toString());
    if (!result) return;
    if (!validate(result)) return;
    onChange(result);
  };

  const getList = useCallback(debounce(async (search: string) => {
    try {
      const res: AxiosResponse<{
        payload: { customers: ICustomerResItem[] };
      }> = await apis.getAllCustomerAutoSearchPage(
        storage.shopId.get(),
        search
      );
      const customerRes = res?.data?.payload?.customers?.sort?.((a, b) => (a.name || '').localeCompare(b.name || ''));
      if (customerRes) {
        setCustomersFilter(customerRes);
      }
    } catch (error) { }
    finally {
      setLoading(false);
    }
  }, 500), []);

  useImperativeHandle(ref, () => ({
    open: () => {
      setOpen(true);
      inputRef.current?.focus();
    },
  }));

  const placeholder = useMemo(() => {
    if (useForService) {
      return !value ? 'Search customer name/phone' : (`${value?.name}`);
    }
    if (v2) {
      return !value ? 'Search customer name/phone' : (`${value?.name} - ${value?.id !== unknownCustomerId ? formatPhone(value?.phone || '') : formatPhone('00000000000')}`);
    }
    return `Customer: ${value?.name || 'Unknown Customer'} ${value?.id !== unknownCustomerId ? `- ${formatPhone(value?.phone || '')}` : `- ${formatPhone('00000000000')}`}`;
  }, [v2, value, useForService]);

  return (
    <WrapperInputSearch className={clsx('WrapperInputSearch container-input', useForService && 'useForService', className)}>
      <AutoComplete
        style={{ width: '100%', height: 'unset' }}
        options={options}
        dropdownRender={(menu) => (<>
          {searchText && loading ? <Row align={'middle'} justify={'center'}><Text py={1.5}><Spin spinning /></Text></Row> : menu}
        </>)}
        notFoundContent={<NotFoundCustomerBox searchText={searchText} />}
        onSelect={handleSwitchCustomer}
        value={searchText}
        open={open}
        onDropdownVisibleChange={setOpen}
        maxLength={autoOpenAddWhenNotFound ? 10 : 11}
        onChange={text => {
          if (text) setLoading(true);
          setSearchText(text);
          getList(text);
        }}
      >
        <Input
          ref={inputRef}
          className={v2 ? 'search-input' : ''}
          suffix={value ? <ButtonClear type='button' onClick={(e: any) => [e.stopPropagation(), onChange(null)]}>
            <IconClose />
          </ButtonClear> : <IconSearch />}
          placeholder={placeholder}
        />
      </AutoComplete>
    </WrapperInputSearch>
  );
});
CustomerSearchInput.displayName = 'CustomerSearchInput';
export default memo(CustomerSearchInput);

const ButtonClear = styled.button`
  z-index: 99;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  inset: 0;
`;

const renderLabel = (name: string, tel: string, _searchWords?: string[]) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
      }}
    >
      <Text variant="CONTENT_2" color="text_3">
        {name}
      </Text>
    </div>
    <div>
      <Text variant="BODY_1" color="text_3">
        {formatPhone(tel)}
      </Text>
    </div>
  </div>
);

const WrapperInputSearch = styled.div` &.container-input {
  flex: 1;
  display: flex;
  align-items: center;
  position: relative;
  input.ant-input::placeholder {
    color: #1D2129;
    font-weight: 600;
  }

  .search-input {
    padding-right: 0;
    height: 40px;
  }

  .ant-input-suffix {
    z-index: 2;
    width: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }
}
`;
